import { olderGeneration, youngerGeneration } from '@/assets/messages';
import {
  AnnotatedType,
  PrimaryAnnotatedType,
  SecondaryAnnotatedType,
} from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { CenteredStoryText, StoryText } from '@/components/microstory/text';
import { SunriseChart, SunriseChartData } from '@/components/sunrise-chart';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { select, t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type ChildhoodStridesChapter1Props = {
  data: ChildhoodStridesData['strides'];
};

export function ChildhoodStridesChapter1(props: ChildhoodStridesChapter1Props) {
  const { data } = props;

  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const [chartData, setChartData] = useState<SunriseChartData>();

  const topic = (topic: string) =>
    t({
      id: 'childhood-strides.sunrise-chart.topic',
      message: select(topic, {
        better_healthcare: 'Have better quality of health care',
        better_clean_water: 'Have greater access to clean water',
        better_education: 'Have better quality of education',
        better_play_opportunities: 'Have better opportunities to play',
        better_healthy_food: 'Have greater access to healthy food',
        better_physical_safety: 'Have improved physical safety',
        better_mental_wellbeing: 'Have better mental well-being',
      }),
    });

  const handleStepEnter = async (
    evt: OnStepEnterExitArgs<keyof ChildhoodStridesData['strides']>
  ) => {
    if (evt.data) {
      const d = data[evt.data];

      const label = t({
        id: 'childhood-strides.sunrise-chart.label',
        message: '% who believe children today {topic}',
        values: { topic: topic(evt.data) },
      });

      setChartData([
        {
          label,
          data: [
            {
              label: youngerGeneration(i18n),
              value: d.younger,
            },
          ],
        },
        {
          data: [
            {
              label: olderGeneration(i18n),
              value: d.older,
            },
          ],
        },
      ]);
    }
  };

  return (
    <>
      <CenteredStoryText>
        <Trans id="childhood-strides.chapter-1.text-1">
          Large majorities of people think <AnnotatedType>childhood has improved</AnnotatedType> in
          most countries across multiple dimensions over the past generation.
        </Trans>
      </CenteredStoryText>

      <ScrollRoot>
        <ScrollGraphic>
          <SunriseChart data={chartData} formatValue={formatPercentage} />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleStepEnter}>
          <ScrollStep data={'better_healthcare'}>
            <StoryText>
              <Trans id="childhood-strides.chapter-1.text-2">
                Across these different dimensions of childhood,{' '}
                <PrimaryAnnotatedType>young</PrimaryAnnotatedType> and{' '}
                <SecondaryAnnotatedType>older people</SecondaryAnnotatedType> alike are most
                convinced of progress in the areas of health&hellip;
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={'better_clean_water'}>
            <StoryText>
              <Trans id="childhood-strides.chapter-1.text-3">&hellip;and clean water.</Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={'better_education'}>
            <StoryText>
              <Trans id="childhood-strides.chapter-1.text-4">
                Progress is also acknowledged across generations — though to a lesser degree — in
                the quality of education…
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={'better_play_opportunities'}>
            <StoryText>
              <Trans id="childhood-strides.chapter-1.text-5">
                &hellip;opportunities to play&hellip;
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={'better_physical_safety'}>
            <StoryText>
              <Trans id="childhood-strides.chapter-1.text-6">&hellip;physical safety&hellip;</Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={'better_healthy_food'}>
            <StoryText>
              <Trans id="childhood-strides.chapter-1.text-7">
                &hellip;and access to healthy food.
              </Trans>
            </StoryText>
          </ScrollStep>

          <ScrollStep data={'better_mental_wellbeing'}>
            <StoryText>
              <Trans id="childhood-strides.chapter-1.text-8">
                <PrimaryAnnotatedType>Young</PrimaryAnnotatedType> and{' '}
                <SecondaryAnnotatedType>old</SecondaryAnnotatedType> are least convinced of progress
                on children's mental well-being.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <CenteredStoryText>
        <Trans id="childhood-strides.chapter-1.text-9">
          These views broadly line up with reality: Over the past three decades, the world has
          recorded <AnnotatedType>stunning progress</AnnotatedType> in expanding access to clean
          water, reducing child malnutrition, providing essential health care, and much more. In
          1990, 9% of children never made it to their fifth birthday. By 2020, that share had
          dropped to less than 4%.
        </Trans>
      </CenteredStoryText>

      <CenteredStoryText>
        <Trans id="childhood-strides.chapter-1.text-10">
          While too many children around the world continue to struggle and are denied their most
          basic needs, it's undeniable that <AnnotatedType>huge gains</AnnotatedType> have been
          achieved for most of the world’s children.
        </Trans>
      </CenteredStoryText>
    </>
  );
}
